import type { MouseEvent, JSX } from 'react';
import { useCallback, useState } from 'react';
import {
  Button,
  datetimeHelpers,
  Icon,
  IconButton,
  LinkButton,
  Text,
  Tooltip,
  useIsHovered,
  useThemeColor,
  useToast,
} from '@wistia/vhs';
import type { DefaultTheme } from 'styled-components';
import styled from 'styled-components';
import { isNotNil } from '~/utilities/type-guards';
import type { NoOpFn } from '~/utilities/util';
import { MemoizedDangerouslyRenderedHtml } from '~/components/MemoizedDangerouslyRendered';
import type { CommentAndRepliesProps, CommentProps, RepliesProps } from './types';
import { CommentHeading } from './CommentHeading';
import { CreateCommentForm } from './Forms/CreateCommentForm';
import { CommentContextMenu } from './CommentContextMenu';
import { UpdateCommentForm } from './Forms/UpdateCommentForm';
import { useCommentContext, commentingViews } from './CommentContext';
import { CommentAvatar } from './CommentAvatar';
import { useToggleTeamCommentResolvedMutation } from './__gql/Comments.gql.gen';
import { MediaStoreSelectors, useMediaStore } from '../Store/MediaStore';
import { CommentReactionSelect } from './CommentReactions/CommentReactionSelect';
import { CommentReactions } from './CommentReactions/CommentReactions';

const { timeAgoString, mediaDurationString } = datetimeHelpers;

const CommentWrapper = styled.div`
  padding: 8px;
  width: auto;
  word-wrap: break-word;
`;

const RepliesContainer = styled.div`
  margin-left: 40px;
`;

const TimeStampWrapper = styled.span`
  display: inline-block;
  margin-right: 5px;
`;

const TimestampText = styled.span<{ hasUnderline: boolean }>`
  color: ${({ theme }) => theme.color.brandBlue500};
  font-weight: 500;
  text-decoration: ${({ hasUnderline }) => (hasUnderline ? 'underline' : 'none')};
`;

const FormWrapper = styled.div`
  display: flex;
  padding: 8px;

  & > form {
    flex-basis: 100%;
  }
`;

const ActionsContainer = styled.div<{ theme: DefaultTheme }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing.space02};
  justify-content: start;
  margin-top: 5px;
`;

const CreatedAt = styled(Text)`
  margin-left: 4px;
`;

const urlRegex =
  /(https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_+.~#?&//=]*))/g;

const CommentAndRepliesWrapper = styled.div<{ isRightOfPlayer: boolean; isActiveComment: boolean }>`
  background-color: ${({ theme, isActiveComment }) => isActiveComment && theme.color.grey200};
  border-radius: 8px;
  margin-bottom: ${({ theme }) => theme.spacing.space02};
  padding: ${({ theme }) => theme.spacing.space02} 0;

  &:hover {
    background-color: ${({ theme, isRightOfPlayer, isActiveComment }) => {
      if (isActiveComment) {
        return theme.color.grey200;
      }
      return isRightOfPlayer ? theme.color.grey100 : '#fff';
    }};
  }
`;

export const MediaTimestamp = ({
  timestamp,
  formattedTimestamp,
  hasUnderline,
  onTimestampClick,
}: {
  timestamp?: number;
  formattedTimestamp: string;
  hasUnderline: boolean;
  onTimestampClick?: () => void;
}): JSX.Element | null => {
  if (timestamp == null) {
    return null;
  }

  return (
    <LinkButton onClick={onTimestampClick}>
      <TimestampText hasUnderline={hasUnderline}>{formattedTimestamp}</TimestampText>
    </LinkButton>
  );
};

const ReplyButton = ({
  onClick,
  isIconBtn,
}: {
  onClick?: NoOpFn;
  isIconBtn: boolean;
}): JSX.Element => {
  const grey500 = useThemeColor('grey500');

  return isIconBtn ? (
    <IconButton
      colorOverride={grey500}
      icon={<Icon type="reply" />}
      label="Reply"
      onClick={(event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        onClick?.();
      }}
      preventTooltip={true}
      size="md"
    />
  ) : (
    <Button
      icon={<Icon type="reply" />}
      label="Reply"
      onClick={(event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        onClick?.();
      }}
      size="sm"
      variant="tertiary"
    />
  );
};

const Comment = ({
  comment,
  isParentComment,
  onClickReply,
  commentHasThread,
  isHovered,
  mediaId,
}: CommentProps): JSX.Element => {
  const [showEditForm, setShowEditForm] = useState(false);
  const { onCommentClick, shouldAllowReplies, commentingView, isAnonymousMedia } =
    useCommentContext();
  const { setHideResolvedComments } = useMediaStore(MediaStoreSelectors.getActions);

  const [green500, grey500, grey700] = useThemeColor(['green500', 'grey500', 'grey700']);

  const {
    id,
    displayName,
    initials,
    createdAt,
    editedAt,
    body,
    bodyHtml,
    mediaTimestamp,
    contact,
    canEdit,
    canDelete,
    canResolveComment,
    resolved,
    commentReactions = [],
  } = comment;

  const permissions = { canEdit, canDelete, canResolveComment };

  const commentBody = bodyHtml ?? body;

  const showToast = useToast();

  const [toggleTeamCommentResolvedMutation] = useToggleTeamCommentResolvedMutation({
    variables: {
      input: {
        teamCommentId: id,
      },
    },
    update: (cache) => {
      cache.evict({
        id: mediaId,
        fieldName: 'topLevelTeamComments',
      });
    },
    onCompleted: () => {
      showToast({
        message: resolved === true ? 'Comment reopened!' : 'Comment resolved',
        variant: 'success',
      });
      setHideResolvedComments(true);
    },
    onError: () => {
      showToast({
        message: 'Something went wrong!',
        variant: 'error',
      });
    },
  });

  const handleTimestampClick = useCallback(() => {
    if (!isParentComment) {
      return;
    }

    onCommentClick?.(id, mediaTimestamp);
  }, [id, isParentComment, mediaTimestamp, onCommentClick]);

  const displayActions = !showEditForm;
  const inTeamView = commentingView === commentingViews.team;
  const reactionsEnabled = inTeamView && !isAnonymousMedia;

  return (
    <CommentWrapper id={id}>
      <CommentHeading>
        <CommentAvatar
          contact={contact}
          savedInitials={initials}
        />
        <Text
          variant="subtitle2"
          style={{ width: '100%' }}
        >
          {displayName}
          <CreatedAt
            variant="caption"
            renderAs="span"
          >
            {timeAgoString(new Date(createdAt))}
          </CreatedAt>
          {isParentComment && inTeamView && canResolveComment && (
            <div style={{ float: 'right', position: 'relative' }}>
              <IconButton
                label={resolved === true ? 'Reopen' : 'Resolve'}
                tooltipDirection="left"
                colorOverride={resolved === true ? green500 : grey500}
                icon={<Icon type="checkmark-circle-outline" />}
                onClick={(event: MouseEvent<HTMLButtonElement>) => {
                  event.preventDefault();
                  void toggleTeamCommentResolvedMutation();
                }}
                size="md"
              />
            </div>
          )}
        </Text>
      </CommentHeading>
      {showEditForm ? (
        <UpdateCommentForm
          initialBody={body}
          commentId={id}
          setShowEditForm={setShowEditForm}
        />
      ) : (
        <Text
          variant="body2"
          style={{ whiteSpace: 'pre-wrap' }}
        >
          {isParentComment && isNotNil(mediaTimestamp) ? (
            <TimeStampWrapper>
              <MediaTimestamp
                formattedTimestamp={mediaDurationString(Math.round(mediaTimestamp) * 1000)}
                timestamp={mediaTimestamp}
                hasUnderline={isHovered}
                onTimestampClick={handleTimestampClick}
              />
            </TimeStampWrapper>
          ) : null}
          {inTeamView ? (
            <MemoizedDangerouslyRenderedHtml
              html={commentBody.replaceAll(
                urlRegex,
                "<a href='$1' target='_blank' rel='noreferrer'>$1</a>",
              )}
              renderInline={true}
            />
          ) : (
            <span>{commentBody}</span>
          )}
          {isNotNil(editedAt) && (
            <Text
              colorOverride={grey700}
              renderAs="span"
              variant="subtitle2"
              style={{ fontWeight: '400' }}
            >
              {' '}
              (edited)
            </Text>
          )}
        </Text>
      )}

      {displayActions && (
        <ActionsContainer>
          {isParentComment && shouldAllowReplies ? (
            <Tooltip
              label="Reply in thread"
              direction="right"
            >
              <ReplyButton
                isIconBtn={true}
                onClick={onClickReply}
              />
            </Tooltip>
          ) : null}
          {reactionsEnabled && <CommentReactionSelect commentId={id} />}
          <CommentContextMenu
            commentHasThread={commentHasThread}
            commentId={id}
            permissions={permissions}
            setShowEditForm={setShowEditForm}
            isParentComment={isParentComment}
            commentTimestamp={mediaTimestamp}
          />
          {reactionsEnabled && (
            <CommentReactions
              commentId={id}
              reactions={commentReactions}
            />
          )}
        </ActionsContainer>
      )}
    </CommentWrapper>
  );
};

const Replies = ({
  replies,
  parentId,
  mediaId,
  contact,
  showReplyForm,
  setShowReplyForm,
  isRightOfPlayer,
  isHovered,
}: RepliesProps): JSX.Element => {
  const [repliesExpanded, setRepliesExpanded] = useState<boolean>(false);
  const { shouldAllowReplies } = useCommentContext();

  const defaultNumReplies = 2;
  const isMoreThanDefaultNumReplies = replies.length > defaultNumReplies;
  const showExpandButton = !repliesExpanded && isMoreThanDefaultNumReplies;
  const repliesToRender =
    repliesExpanded || !isMoreThanDefaultNumReplies ? replies : replies.slice(-defaultNumReplies);
  const hiddenReplyCount = replies.length - defaultNumReplies;

  const showReplyButton = replies.length > 0 && shouldAllowReplies;

  return (
    <RepliesContainer>
      {showExpandButton ? (
        <Button
          label={`Show ${hiddenReplyCount} More Comment${hiddenReplyCount > 1 ? 's' : ''}`}
          variant="tertiary"
          size="md"
          iconName="expand"
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            setRepliesExpanded(true);
          }}
        />
      ) : null}
      {repliesToRender.map((comment) => {
        return (
          <Comment
            key={comment.id}
            comment={comment}
            commentHasThread={false}
            isParentComment={false}
            isHovered={isHovered}
          />
        );
      })}
      {showReplyForm ? (
        <FormWrapper>
          <CreateCommentForm
            contact={contact}
            hideForm={() => setShowReplyForm(false)}
            isReply={true}
            isRightOfPlayer={isRightOfPlayer}
            parentId={parentId}
            mediaId={mediaId}
          />
        </FormWrapper>
      ) : (
        <div>
          {showReplyButton ? (
            <ReplyButton
              isIconBtn={false}
              onClick={() => setShowReplyForm(true)}
            />
          ) : null}
        </div>
      )}
    </RepliesContainer>
  );
};

export const CommentAndReplies = ({
  topLevelComment,
  mediaId,
  contact,
  isRightOfPlayer,
}: CommentAndRepliesProps): JSX.Element => {
  const [isHovered] = useIsHovered();
  const [showReplyForm, setShowReplyForm] = useState<boolean>(false);
  const { id, replies } = topLevelComment;
  const commentHasThread = replies.length > 0;

  const { activeCommentId } = useCommentContext();

  return (
    <CommentAndRepliesWrapper
      isActiveComment={activeCommentId === topLevelComment.id}
      isRightOfPlayer={isRightOfPlayer}
    >
      <Comment
        comment={topLevelComment}
        commentHasThread={commentHasThread}
        onClickReply={() => setShowReplyForm(true)}
        isParentComment={true}
        isHovered={isHovered}
        mediaId={mediaId}
      />
      <Replies
        contact={contact}
        parentId={id}
        replies={replies}
        mediaId={mediaId}
        setShowReplyForm={setShowReplyForm}
        showReplyForm={showReplyForm}
        isRightOfPlayer={isRightOfPlayer}
        isHovered={isHovered}
      />
    </CommentAndRepliesWrapper>
  );
};
